
import { io, Socket } from 'socket.io-client';

// Define event types
export interface UserLocation {
    url: string;
    title: string;
}

export interface ChatMessageData {
    to?: string; // admin or specific user
    text: string;
}

class SocketService {
    private socket: Socket | null = null;
    private static instance: SocketService;

    private constructor() { }

    public static getInstance(): SocketService {
        if (!SocketService.instance) {
            SocketService.instance = new SocketService();
        }
        return SocketService.instance;
    }

    public connect() {
        if (this.socket?.connected) return;

        // In dev, usually localhost:5000. In prod, same origin.
        // We can use '' to auto-detect, assuming API is on same host/port or proxied.
        // If frontend is 5173 and backend 5000, we need explicit URL.
        // With Vite proxy configured for /socket.io, we can use relative path '/'
        // This works for both dev (proxy -> 3002) and prod (same origin)
        const url = '/';

        this.socket = io(url, {
            path: '/socket.io',
            withCredentials: true,
            transports: ['websocket', 'polling']
        });

        this.socket.on('connect', () => {
            console.log('🟢 Connected to socket server:', this.socket?.id);
        });

        this.socket.on('disconnect', () => {
            console.log('🔴 Disconnected from socket server');
        });
    }

    public disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    // Emit events
    public emitLocationUpdate(location: UserLocation) {
        this.socket?.emit('location_update', location);
    }

    public registerAdmin() {
        this.socket?.emit('register_admin');
    }

    public sendMessage(text: string, to: string = 'admin') {
        this.socket?.emit('send_message', { text, to });
    }

    public joinAdminSession(sessionId: string) {
        this.socket?.emit('admin_join_session', sessionId);
    }

    public getSessions() {
        this.socket?.emit('get_sessions');
    }

    public markRead(sessionId: string) {
        this.socket?.emit('mark_read', sessionId);
    }

    public takeoverSession(sessionId: string) {
        // Transition session from AI to agent control
        this.socket?.emit('takeover_session', sessionId);
    }

    public returnToAI(sessionId: string) {
        // Return session control back to AI
        this.socket?.emit('return_to_ai', sessionId);
    }

    /**
     * Sync chat session to backend (REST API → MongoDB persistence).
     * Called by GlobalChatWidget when requesting a human agent.
     */
    public async syncSession(sessionData: {
        id: string;
        userId: string;
        messages: { sender: string; text: string; timestamp: number }[];
        status: string;
        language?: string;
        userName?: string;
        userEmail?: string;
    }): Promise<void> {
        try {
            const res = await fetch('/api/chat/sync', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sessionData)
            });
            if (!res.ok) {
                console.warn('[SocketService] Chat sync failed:', res.status);
            } else {
                console.log('[SocketService] ✅ Chat session synced to backend');
            }
        } catch (err) {
            console.warn('[SocketService] Chat sync error:', err);
        }
    }

    // Listeners
    public onMessage(callback: (msg: any) => void) {
        this.socket?.on('new_message', callback);
    }

    public onUserListUpdate(callback: (users: any[]) => void) {
        this.socket?.on('users_online', callback);
    }

    public onSessionsUpdate(callback: (sessions: any[]) => void) {
        this.socket?.on('sessions_update', callback);
    }

    public off(event: string) {
        this.socket?.off(event);
    }
}

export const socketService = SocketService.getInstance();
